// src/features/S1Metadata/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  DatePickerField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";
import {
  dateSchema,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { Tables } from "@/gen/tableNames/tables";

import { Clients } from "@/lib/clients";

const FormSchema = z.object({
  strategy_id: z.coerce.number(),
  strategy_name: z.union([
    z.string(),
    z.object({ text: z.string(), id: z.number() }),
  ]),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  comment_type: z.string().nullable().optional(),
  s1_time_serie_file_id: z.number().nullable().optional(),
  time_serie_file_id: z.number().nullable().optional(),
});

const formSchemaFields = FormSchema.keyof().enum;

export function S1MonthlyCommentsEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
    translations,
  } = useEditForm({
    id,
    client: Clients.S1_QUANTILIA,
    table_name: Tables.S1_MONTHLY_COMMENTS,
    FormSchema,
    fieldsNotApplyTranslation: ["comment_type"],
    adjustData: (data) => {
      const {
        created_at,
        updated_at,
        strategy_name,
        s1_time_serie_file_id,
        time_serie_file_id,
        ...payload
      } = data;
      return payload;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Monthly comment {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name={formSchemaFields.strategy_name}
                  label={t(
                    `${Tables.S1_MONTHLY_COMMENTS}.columns.strategy_name`,
                    {
                      ns: "tables",
                    },
                  )}
                  base_url={`${Clients.S1_QUANTILIA}/${Tables.S1_MONTHLY_COMMENTS}`}
                  column_name="name"
                  id_column={formSchemaFields.strategy_id}
                  control={form.control}
                  isRequired={true}
                  options={{
                    autoSelectSingle: true,
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`${Tables.S1_MONTHLY_COMMENTS}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <SelectField
                    name={formSchemaFields.comment_type}
                    label={t(
                      `${Tables.S1_MONTHLY_COMMENTS}.columns.comment_type`,
                      { ns: "tables", defaultValue: "Comment Type" },
                    )}
                    options={translations?.comment_type}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
                <TextareaField
                  name={formSchemaFields.comment}
                  label={t(`${Tables.S1_MONTHLY_COMMENTS}.columns.comment`, {
                    ns: "tables",
                  })}
                  isRequired={false}
                  control={form.control}
                />
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={Clients.S1_QUANTILIA}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel creation modal */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
